import React, { useEffect, useRef, useState } from 'react';
import { Formatter, Renderer, Stave, StaveNote, StaveTie, Voice } from 'vexflow';
import type { GhostNote } from 'vexflow';
import type { MusicalToken } from '../../types/music';
import type { TimeSignature } from '../../types/music';
import type { SymbolAnalysisHighlight } from '../../types/payloads/symbol_analysis.types';
import type { RhythmicCell } from '../rhythmicCellEngine';
import { tokenToLabel, tokenToSymbol } from '../tokens';
import { ensureSalaVexFonts, resetSalaVexMetricsForPrint } from './vexFonts';
import { applyPrintSvgInk } from './vexPrintInk';
import { fitHostSvgToStave } from './vexSvgFit';
import { VexRhythmGlyph } from './VexRhythmGlyph';
import {
  mapEventsToVexSpecs,
  mapPatternToVexSpecs,
  mapRhythmicCellToVexSpecs,
  truncateSpecsToBarCapacity,
  truncateTokensToBarCapacity,
  type ScoreRenderEvent,
  type VexNoteSpec,
} from './vexFlowMapper';
import {
  createBlankGhostNote,
  drawBeatCellReadingLabels,
  drawBeamsForPattern,
  drawBlankGhostSlot,
  type VexCellRange,
} from './vexBeamBuilder';
import { cellRangesFromBeatCells } from './phraseBarLayout';
import { buildBarBeatStructure } from '../rhythmicCellEngine';
import { createNoteFromSpec, styleNoteDots } from './vexNoteRender';
import { VEX_PRINT, VEX_SCREEN } from './vexPrintTheme';
import { tiesFromScoreEvents } from './vexTieUtils';

interface VexRhythmStripProps {
  pattern: MusicalToken[];
  timeSignature?: TimeSignature;
  missingIndex?: number;
  showLabels?: boolean;
  readingMethod?: 'tradicional' | 'takadimi' | 'kodaly';
  printMode?: boolean;
  className?: string;
  /** Oculta la etiqueta de compás (cuando el padre ya la muestra). */
  hideMeterLabel?: boolean;
  targetBarNumber?: number;
  /** Índices de figuras con puntillo (desde el parser MusicXML). */
  dottedIndices?: number[];
  /** Pares [fromIndex, toIndex] para ligaduras de prolongación. */
  ties?: [number, number][];
  /** Clave VexFlow: G, F, Bb, etc. */
  keySignature?: string;
  /** Marca pedagógica sobre un símbolo (letra A, B…). */
  highlight?: SymbolAnalysisHighlight;
  /** Células por tiempo del compás — prioridad sobre pattern plano. */
  beatCells?: RhythmicCell[];
  /** Células planas legacy (alias de beatCells si no se pasa beatCells). */
  rhythmicCells?: RhythmicCell[];
  /** Dibuja clave de Sol al inicio del pentagrama. */
  showClef?: boolean;
  /** Eventos parseados del MusicXML — render fiel con pitch y ligaduras. */
  scoreEvents?: ScoreRenderEvent[];
}

function beatsFromTimeSignature(ts: TimeSignature): { numBeats: number; beatValue: number } {
  const [n, d] = ts.split('/').map(Number);
  return { numBeats: n || 4, beatValue: d || 4 };
}

function maxBarBeats(timeSignature: TimeSignature): number {
  const { numBeats, beatValue } = beatsFromTimeSignature(timeSignature);
  return numBeats * (4 / beatValue);
}

function createNoteFromSpecForStrip(spec: VexNoteSpec, color: string, printMode: boolean): StaveNote {
  return createNoteFromSpec(spec, color, printMode);
}

function buildSpecsFromBeatCells(
  beatCells: RhythmicCell[],
  timeSignature: TimeSignature,
  missingBeatIndex?: number,
): { specs: VexNoteSpec[]; blankSpecIndices: number[]; cellRanges: VexCellRange[] } {
  const specs: VexNoteSpec[] = [];
  const blankSpecIndices: number[] = [];
  const cellRanges: VexCellRange[] = [];

  for (let beatIdx = 0; beatIdx < beatCells.length; beatIdx += 1) {
    const cell = beatCells[beatIdx]!;
    if (cell.modifiers?.isSpanContinuation || cell.modifiers?.isPadding) continue;

    const start = specs.length;

    if (beatIdx === missingBeatIndex) {
      blankSpecIndices.push(specs.length);
      specs.push({ duration: 'q', keys: cell.isRest ? ['b/4'] : ['f/4'], isRest: false, isBlank: true });
      cellRanges.push({ start, end: specs.length, beatIndex: beatIdx });
      continue;
    }

    specs.push(...mapRhythmicCellToVexSpecs(cell, cell.modifiers?.hasDot === true));
    cellRanges.push({ start, end: specs.length, beatIndex: beatIdx });
  }

  const capped = truncateSpecsToBarCapacity(specs, maxBarBeats(timeSignature));
  const trimmedRanges = cellRanges
    .map((range) => ({
      ...range,
      end: Math.min(range.end, capped.length),
    }))
    .filter((range) => range.start < range.end && range.start < capped.length);

  return {
    specs: capped,
    blankSpecIndices: blankSpecIndices.filter((idx) => idx < capped.length),
    cellRanges: trimmedRanges,
  };
}

function buildSpecsFromScoreEvents(
  events: ScoreRenderEvent[],
  timeSignature: TimeSignature,
  missingIndex?: number,
): { specs: VexNoteSpec[]; blankSpecIndices: number[]; cellRanges: VexCellRange[] } {
  const mapped = mapEventsToVexSpecs(events);
  let specs = truncateSpecsToBarCapacity(mapped.specs, maxBarBeats(timeSignature));
  const blankSpecIndices: number[] = [];
  let cellRanges: VexCellRange[] = [];

  const tokens = events.map((e) => e.token);
  const { beatCells } = buildBarBeatStructure(tokens, timeSignature);
  cellRanges = cellRangesFromBeatCells(beatCells, specs.length);

  if (missingIndex != null && missingIndex >= 0 && missingIndex < specs.length) {
    const blankKey = specs[missingIndex]?.keys?.[0] ?? 'f/4';
    specs = [...specs];
    specs[missingIndex] = {
      duration: specs[missingIndex]?.duration ?? 'q',
      keys: [blankKey],
      isRest: false,
      isBlank: true,
    };
    blankSpecIndices.push(missingIndex);
  }

  return { specs, blankSpecIndices, cellRanges };
}

function buildSpecsFromInput(
  pattern: MusicalToken[],
  timeSignature: TimeSignature,
  beatCells: RhythmicCell[] | undefined,
  rhythmicCells: RhythmicCell[] | undefined,
  dottedIndices: number[],
  missingIndex?: number,
  scoreEvents?: ScoreRenderEvent[],
): { specs: VexNoteSpec[]; blankSpecIndices: number[]; cellRanges: VexCellRange[] } {
  if (scoreEvents?.length) {
    const built = buildSpecsFromScoreEvents(scoreEvents, timeSignature, missingIndex);
    return { ...built, cellRanges: [] };
  }

  const cells = beatCells?.length ? beatCells : rhythmicCells;
  if (cells?.length) {
    return buildSpecsFromBeatCells(cells, timeSignature, missingIndex);
  }

  const truncated = truncateTokensToBarCapacity(pattern, timeSignature);
  const { specs } = mapPatternToVexSpecs(truncated, dottedIndices);
  const capped = truncateSpecsToBarCapacity(specs, maxBarBeats(timeSignature));

  if (missingIndex == null || missingIndex < 0 || missingIndex >= truncated.length) {
    return { specs: capped, blankSpecIndices: [], cellRanges: [] };
  }

  let blankSpecIndex = 0;
  for (let i = 0; i < missingIndex; i += 1) {
    const slice = mapPatternToVexSpecs([truncated[i]!], dottedIndices.includes(i) ? [0] : []);
    blankSpecIndex += slice.specs.length;
  }

  const out = [...capped];
  if (blankSpecIndex >= 0 && blankSpecIndex < out.length) {
    out[blankSpecIndex] = { duration: 'q', keys: ['f/4'], isRest: false, isBlank: true };
    return { specs: out, blankSpecIndices: [blankSpecIndex], cellRanges: [] };
  }
  return { specs: out, blankSpecIndices: [], cellRanges: [] };
}

function drawCrossBarTieIn(
  ctx: ReturnType<Renderer['getContext']>,
  note: StaveNote,
  color: string,
  printMode: boolean,
) {
  let x = 0;
  let y = 40;
  try {
    if (note.getAbsoluteX) x = note.getAbsoluteX();
    const ys = note.getYs?.();
    if (ys?.length) y = ys[0]!;
  } catch {
    /* fallback */
  }
  const stroke = printMode ? '#0a0a0a' : color;
  ctx.setStrokeStyle(stroke);
  ctx.setLineWidth(1.5);
  ctx.beginPath();
  ctx.moveTo(x - 6, y + 2);
  ctx.bezierCurveTo(x - 22, y + 2, x - 22, y - 10, x - 6, y - 10);
  ctx.stroke();
}

function drawCrossBarTieOut(
  ctx: ReturnType<Renderer['getContext']>,
  note: StaveNote,
  color: string,
  printMode: boolean,
) {
  let x = 0;
  let y = 40;
  try {
    if (note.getAbsoluteX) x = note.getAbsoluteX() + 12;
    const ys = note.getYs?.();
    if (ys?.length) y = ys[0]!;
  } catch {
    /* fallback */
  }
  const stroke = printMode ? '#0a0a0a' : color;
  ctx.setStrokeStyle(stroke);
  ctx.setLineWidth(1.5);
  ctx.beginPath();
  ctx.moveTo(x + 6, y + 2);
  ctx.bezierCurveTo(x + 22, y + 2, x + 22, y - 10, x + 6, y - 10);
  ctx.stroke();
}

function noteCenterX(stave: Stave, notes: StaveNote[], index: number, total: number): number {
  const noteStart = stave.getNoteStartX();
  const noteEnd = stave.getNoteEndX();
  const slotWidth = (noteEnd - noteStart) / Math.max(total, 1);
  const note = notes[index];
  if (note?.getAbsoluteX) {
    try {
      return note.getAbsoluteX() + 8;
    } catch {
      /* fallback */
    }
  }
  return noteStart + index * slotWidth + slotWidth * 0.5;
}

function drawHighlightMarker(
  ctx: ReturnType<Renderer['getContext']>,
  stave: Stave,
  notes: StaveNote[],
  patternLen: number,
  highlight: SymbolAnalysisHighlight | undefined,
  ties: [number, number][],
  color: string,
  printMode: boolean,
) {
  if (!highlight?.label) return;
  const label = highlight.label;
  const markerColor = printMode ? '#c2410c' : '#fb923c';
  ctx.setFont('Arial, sans-serif', 12, 'bold');
  ctx.setFillStyle(markerColor);

  let x = stave.getNoteStartX() + 12;
  let y = stave.getYForLine(0) - 6;

  if (highlight.type === 'key_signature') {
    x = stave.getX() + 18;
    y = stave.getYForLine(1) - 4;
  } else if (highlight.type === 'tie') {
    const pair = ties.find(([a]) => a === (highlight.tokenIndex ?? 0)) ?? ties[0];
    if (pair && notes[pair[0]] && notes[pair[1]]) {
      x = (noteCenterX(stave, notes, pair[0], patternLen) + noteCenterX(stave, notes, pair[1], patternLen)) / 2;
      y = stave.getYForLine(0) - 10;
    }
  } else if (highlight.type === 'dot' && highlight.tokenIndex != null) {
    x = noteCenterX(stave, notes, highlight.tokenIndex, patternLen);
    y = stave.getYForLine(0) - 8;
  }

  ctx.fillText(label, x - 4, y);

  ctx.setStrokeStyle(markerColor);
  ctx.setLineWidth(1);
  const radius = 10;
  ctx.beginPath();
  ctx.arc(x + 2, y - 6, radius, 0, Math.PI * 2);
  ctx.stroke();
}

/** Pista rítmica con pentagrama VexFlow — compás real en pantalla e impresión. */
export function VexRhythmStrip({
  pattern,
  timeSignature = '4/4',
  missingIndex,
  showLabels = false,
  readingMethod = 'tradicional',
  printMode = false,
  className = '',
  hideMeterLabel = false,
  targetBarNumber,
  dottedIndices = [],
  ties = [],
  keySignature,
  highlight,
  beatCells,
  rhythmicCells,
  showClef = true,
  scoreEvents,
}: VexRhythmStripProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const color = printMode ? VEX_PRINT.noteColor : '#f4f0e8';
  const theme = printMode ? VEX_PRINT : VEX_SCREEN;
  const resolvedBeatCells = beatCells?.length ? beatCells : rhythmicCells;

  useEffect(() => {
    if (failed || (!pattern.length && !resolvedBeatCells?.length)) return;
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;

    (async () => {
      try {
        await ensureSalaVexFonts();
        if (printMode) resetSalaVexMetricsForPrint();
        if (cancelled) return;

        host.innerHTML = '';

        const { specs, blankSpecIndices, cellRanges } = buildSpecsFromInput(
          pattern,
          timeSignature,
          beatCells,
          rhythmicCells,
          dottedIndices,
          missingIndex,
          scoreEvents,
        );
        if (!specs.length) return;

        const labelPad = showLabels ? theme.labelBottomPad : 0;
        const noteWidth = printMode ? 34 : 56;
        const clefReserve = showClef ? (printMode ? 42 : 44) : 0;
        const keySigReserve = keySignature && keySignature !== 'C' ? (printMode ? 48 : 52) : 0;
        const timeSigReserve = printMode ? 58 : 72;
        const staveWidth = Math.max(
          printMode ? 200 : 220,
          clefReserve + keySigReserve + timeSigReserve + specs.length * noteWidth + (printMode ? 24 : 32),
        );
        const staveX = printMode ? 10 : 10;
        const staveY = theme.staveY;
        const canvasW = staveX + staveWidth + (printMode ? 18 : 24);
        const canvasH = theme.canvasHeight + (showLabels ? labelPad : 0);

        const renderer = new Renderer(host, Renderer.Backends.SVG);
        renderer.resize(canvasW, canvasH);
        const ctx = renderer.getContext();

        const stave = new Stave(staveX, staveY, staveWidth);
        if (showClef) {
          try {
            stave.addClef('treble');
          } catch {
            /* clave no disponible */
          }
        }
        if (keySignature && keySignature !== 'C') {
          try {
            stave.addKeySignature(keySignature);
          } catch {
            /* clave no reconocida */
          }
        }
        const [top, bottom] = timeSignature.split('/');
        stave.addTimeSignature(`${top}/${bottom}`);
        stave.setContext(ctx);
        stave.draw();

        const notes = specs.map((spec) =>
          spec.isBlank ? createBlankGhostNote(spec) : createNoteFromSpecForStrip(spec, color, printMode),
        );
        const staveNotes = notes.filter((n): n is StaveNote => n instanceof StaveNote);

        if (notes.length) {
          const { numBeats, beatValue } = beatsFromTimeSignature(timeSignature);
          const voice = new Voice({ numBeats, beatValue });
          voice.setStrict(false);
          voice.addTickables(notes);
          const formatWidth = Math.max(80, stave.getNoteEndX() - stave.getNoteStartX() - 8);
          new Formatter().joinVoices([voice]).format([voice], formatWidth);
          notes.forEach((n, i) => {
            n.setStave(stave);
            if (!specs[i]?.isBlank && n instanceof StaveNote) {
              n.draw();
              if (specs[i]?.dotted) styleNoteDots(n, color, ctx);
            }
          });

          drawBeamsForPattern(ctx, notes, specs, cellRanges);

          for (const blankIdx of blankSpecIndices) {
            const blankNote = notes[blankIdx];
            if (blankNote) drawBlankGhostSlot(ctx, stave, blankNote, printMode);
          }

          if (showLabels && resolvedBeatCells?.length && cellRanges.length) {
            drawBeatCellReadingLabels(
              ctx,
              stave,
              notes,
              cellRanges,
              resolvedBeatCells,
              readingMethod,
              printMode,
              missingIndex,
            );
          }

          for (const [from, to] of ties.length ? ties : scoreEvents?.length ? tiesFromScoreEvents(scoreEvents) : []) {
            if (from >= 0 && to < notes.length && notes[from] instanceof StaveNote && notes[to] instanceof StaveNote) {
              try {
                const tie = new StaveTie({
                  firstNote: notes[from]!,
                  lastNote: notes[to]!,
                  firstIndexes: [0],
                  lastIndexes: [0],
                });
                tie.setContext(ctx);
                if (printMode) {
                  tie.setStyle({ strokeStyle: '#0a0a0a', lineWidth: 1.5 });
                } else {
                  tie.setStyle({ strokeStyle: color, lineWidth: 1.5 });
                }
                tie.draw();
              } catch {
                /* par inválido */
              }
            }
          }

          if (specs[0]?.tieInFromPrevious && notes[0] instanceof StaveNote) {
            drawCrossBarTieIn(ctx, notes[0], color, printMode);
          }
          const lastIdx = specs.length - 1;
          if (lastIdx >= 0 && specs[lastIdx]?.tieOutToNext && notes[lastIdx] instanceof StaveNote) {
            drawCrossBarTieOut(ctx, notes[lastIdx]!, color, printMode);
          }

          drawHighlightMarker(ctx, stave, staveNotes, specs.length, highlight, ties, color, printMode);
        }

        fitHostSvgToStave(host, stave, canvasW, {
          top: 28,
          bottom: showLabels ? theme.labelBottomPad + 8 : 20,
          left: 8,
          right: 14,
        });

        if (printMode) applyPrintSvgInk(host, color);

        const svg = host.querySelector('svg');
        if (svg) {
          svg.classList.add('vex-rhythm-strip__svg');
          if (printMode) {
            svg.classList.add('vex-rhythm-strip__svg--print');
            svg.style.height = `${VEX_PRINT.svgHeight}px`;
            svg.style.width = 'auto';
            svg.style.maxWidth = '100%';
          }
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    pattern,
    timeSignature,
    missingIndex,
    color,
    failed,
    printMode,
    dottedIndices,
    ties,
    keySignature,
    highlight,
    beatCells,
    rhythmicCells,
    showClef,
    scoreEvents,
    showLabels,
    readingMethod,
    resolvedBeatCells,
  ]);

  if (failed) {
    const meterLabel = targetBarNumber
      ? `Compás ${targetBarNumber} (${timeSignature})`
      : `Compás ${timeSignature}`;
    return (
      <div className={`vex-rhythm-strip-fallback ${className}`.trim()}>
        {!hideMeterLabel && <p className="print-rhythm-pattern__meter">{meterLabel}</p>}
        <div className="print-rhythm-pattern__row">
          {pattern.map((token, i) => (
            <div key={i} className="print-rhythm-pattern__cell">
              {missingIndex === i ? (
                <span className="print-rhythm-pattern__missing">?</span>
              ) : (
                <span className="print-rhythm-pattern__symbol">{tokenToSymbol(token)}</span>
              )}
              {showLabels && (
                <span className="print-rhythm-pattern__label">{tokenToLabel(token, readingMethod)}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`vex-rhythm-strip ${printMode ? 'vex-rhythm-strip--print' : ''} ${className}`.trim()}>
      {!hideMeterLabel && (
        <p className="music-rhythm-track__meter">
          {targetBarNumber ? `Compás ${targetBarNumber} (${timeSignature})` : `Compás ${timeSignature}`}
        </p>
      )}
      <div ref={hostRef} className="vex-rhythm-strip__host" />
      {missingIndex != null && missingIndex >= 0 && !printMode && !resolvedBeatCells?.length && (
        <div className="vex-rhythm-strip__missing" aria-label="Figura faltante">
          <span>Casillero {missingIndex + 1}:</span>
          <span className="print-rhythm-pattern__missing">?</span>
        </div>
      )}
      {showLabels && !resolvedBeatCells?.length && (
        <div className="vex-rhythm-strip__labels">
          {pattern.map((token, i) => (
            <span key={i} className="vex-rhythm-strip__label">
              {i === missingIndex ? '?' : tokenToLabel(token, readingMethod)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Fila de glifos sueltos (opciones MCQ). */
export function VexRhythmGlyphRow({
  tokens,
  size = 36,
  printMode = false,
}: {
  tokens: MusicalToken[];
  size?: number;
  printMode?: boolean;
}) {
  return (
    <div className="vex-rhythm-glyph-row">
      {tokens.map((token, i) => (
        <VexRhythmGlyph key={`${token}-${i}`} token={token} size={size} printMode={printMode} />
      ))}
    </div>
  );
}
